import { useRef, useCallback, useSyncExternalStore } from 'react'

/**
 * Counts down from `totalSeconds` to 0, ticking once per second.
 * Derives remaining time from the wall clock for resilience against
 * missed ticks, tab throttling, and strict mode double-mounting.
 *
 * @param totalSeconds - number of seconds to count down from
 * @param enabled - whether the countdown is active (defaults to true)
 * @returns remaining seconds (clamped to 0)
 */
export const useCountdown = (totalSeconds: number, enabled = true): number => {
  const endTimeRef = useRef<number | null>(null)

  const subscribe = useCallback(
    (onTick: () => void) => {
      if (!enabled) return () => {}
      endTimeRef.current = Date.now() + totalSeconds * 1000
      const id = setInterval(onTick, 1000)
      return () => clearInterval(id)
    },
    [enabled, totalSeconds]
  )

  const getSnapshot = useCallback(() => {
    if (!enabled || endTimeRef.current === null) return totalSeconds
    return Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000))
  }, [enabled, totalSeconds])

  return useSyncExternalStore(subscribe, getSnapshot)
}
